import React, { useEffect } from "react";
import Select from "react-select";
import { onlyNumericInput, calculateRentalDuration, comma, uncomma } from "@/utils";
import { originOptions, carYearOptions, chargeRateOptions } from "@/constants/selectOptions";
import { useGetInsuranceCorpQuery } from "@/redux/insuranceCorpApi";
import { useGetRentPriceTableQuery, useLazyGetRentPriceTableQuery } from "@/redux/metaApi";
import {
    useGetCarClassQuery,
    useGetCarGubunQuery,
    useGetCarManufacturerQuery,
    useGetCarModelDetailQuery,
    useGetCarModelQuery,
    useGetCarTrimQuery,
    useGetFuelListQuery,
} from "@/redux/metaApi";
import {formatPhoneNumber} from "../../../utils";

export const BasicInfo = ({ formData, handleChange, setFormData, showInsuranceManager }) => {
    const { accidentCar, contracts, contractInsurance, customerName, customerPhone } = formData;

    // 차종 옵션 (독립)
    const { data: carGubunData } = useGetCarGubunQuery();
    const carGubunOptions =
        carGubunData && carGubunData.data
            ? carGubunData.data.map((item) => ({
                  value: item.bgNo,
                  label: item.bgName,
              }))
            : [];

    // 제조사
    const { data: manufacturerData } = useGetCarManufacturerQuery({ bmNo: null });
    const manufacturerOptions =
        manufacturerData?.data?.map((item) => ({
            value: item.bmNo,
            label: item.bmName,
        })) || [];

    // 모델 (skip: !manufacturer)
    const { data: modelData } = useGetCarModelQuery({ bmNo: accidentCar?.bmNo }, { skip: !accidentCar?.bmNo });
    const modelOptions =
        modelData?.data?.map((item) => ({
            value: item.boiNo,
            label: item.boiName,
        })) || [];

    // 상세모델 (skip: !model)
    const { data: detailModelData } = useGetCarModelDetailQuery({ boiNo: accidentCar?.boiNo }, { skip: !accidentCar?.boiNo });
    const detailModelOptions =
        detailModelData?.data?.map((item) => ({
            value: item.boNo,
            label: item.boName,
        })) || [];

    // 등급 (skip: !detailModel)
    const { data: carClassData } = useGetCarClassQuery({ boNo: accidentCar?.boNo }, { skip: !accidentCar?.boNo });
    const carClassOptions =
        carClassData?.data?.map((item) => ({
            value: item.bsNo,
            label: item.bsName,
        })) || [];

    // 트림 (skip: !carClass)
    const { data: carTrimData } = useGetCarTrimQuery({ bsNo: accidentCar?.bsNo }, { skip: !accidentCar?.bsNo });
    const carTrimOptions =
        carTrimData?.data?.map((item) => ({
            value: item.bdNo,
            label: item.bdName,
        })) || [];

    // 유종 옵션 : getFuelList API
    const { data: fuelData } = useGetFuelListQuery();
    const fuelOptions =
        fuelData && fuelData.data
            ? fuelData.data.map((item) => ({
                  value: item.bfNo,
                  label: item.bfName,
              }))
            : [];

    // API 호출: 보험사 업체 조회
    const { data: insuranceCorpData } = useGetInsuranceCorpQuery();
    const insuranceCorpOptions =
        insuranceCorpData && insuranceCorpData.data
            ? insuranceCorpData.data.map((item) => ({
                  value: item.id,
                  label: item.name,
                  receiptNumberPrefix: item.receiptNumberPrefix,
              }))
            : [];

    useEffect(() => {
        let totalDays = 0;
        let totalHours = 0;
        let totalRentFee = 0;
        let totalChargeFee = 0;

        if (Array.isArray(contracts) && contracts.length > 0) {
            contracts.forEach((contract) => {
                const { allocation, billingInfo } = contract;
                // 대여기간 계산: allocation.releasedAt와 allocation.returnedAt이 있을 때
                if (allocation && allocation?.releasedAt && allocation?.returnedAt) {
                    const { days, hours } = calculateRentalDuration(new Date(allocation?.releasedAt), new Date(allocation?.returnedAt));
                    totalDays += days;
                    totalHours += hours;
                }
                // 렌트 요금: billingInfo.rentFee
                if (billingInfo && billingInfo.rentFee) {
                    totalRentFee += Number(uncomma(billingInfo.rentFee)) || 0;
                }
                // 청구 요금: billingInfo.chargeFee
                if (billingInfo && billingInfo.chargeFee) {
                    totalChargeFee += Number(uncomma(billingInfo.chargeFee)) || 0;
                }
            });

            // 만약 총 시간(totalHours)이 24시간 이상이면 추가 일수로 변환
            if (totalHours >= 24) {
                const extraDays = Math.floor(totalHours / 24);
                totalDays += extraDays;
                totalHours = totalHours % 24;
            }

            // formData.contractInsurance 결과 세팅 (contractInsurance가 기존에 없으면 새 객체 생성)
            setFormData((prev) => ({
                ...prev,
                contractInsurance: {
                    ...prev.contractInsurance,
                    totalRentalPeriod: `${totalDays}일 ${totalHours}시간`,
                    days: totalDays,
                    hours: totalHours,
                    totalRentFee,
                    totalChargeFee,
                },
            }));
        }
    }, [contracts, setFormData]);

    // 요금표 목록 조회
    const { data: rentPriceTableData } = useGetRentPriceTableQuery();
    const rentPriceTableOptions = [
        { value: "INSURANCE_BASE", label: "보험사기준" },
        ...(rentPriceTableData && rentPriceTableData.data
            ? rentPriceTableData.data.map((item) => ({
                  value: item.id,
                  label: item.name,
              }))
            : []),
        { value: "INPERSON", label: "직접입력" },
    ];

    // lazy hook 선언: INSURANCE_BASE 선택 시 API 호출에 사용
    const [triggerGetRentPriceTable] = useLazyGetRentPriceTableQuery();

    return (
        <>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>고객명</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="customerName" placeholder="" value={customerName || ""} onChange={handleChange} disabled />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>고객 연락처</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="customerPhone"
                            placeholder=""
                            value={formatPhoneNumber(customerPhone || "")}
                            onChange={handleChange}
                            disabled
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>

            {/* 차량번호 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        차량번호 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="accidentCar.carNumber"
                            placeholder=""
                            value={accidentCar?.carNumber || ""}
                            onChange={handleChange}
                            disabled
                        />
                    </div>
                </div>
            </div>

            {/* 모델 (제조사 선택에 종속) */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        모델 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="모델 선택"
                            options={modelOptions}
                            value={modelOptions?.find((opt) => opt.value === accidentCar?.boiNo)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    accidentCar: {
                                        ...accidentCar,
                                        boiNo: option.value,
                                        boNo: "",
                                        bsNo: "",
                                        bdNo: "",
                                    },
                                }))
                            }
                            isDisabled
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>
            {/* 보험사 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        보험사 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={insuranceCorpOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={true}
                            placeholder="보험사 선택"
                            value={insuranceCorpOptions.find((opt) => opt.label === contractInsurance?.insuranceCorpName)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    contractInsurance: {
                                        ...prev.contractInsurance,
                                        insuranceCorpId: option.value,
                                        insuranceCorpName: option.label,
                                        // receiptNumber: option.receiptNumberPrefix,
                                        prefixNumber: option.receiptNumberPrefix,
                                    },
                                }));
                            }}
                            isDisabled
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        접수번호 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    {/* <div className="form-element">
                        <input
                            type="text"
                            name="contractInsurance.receiptNumber"
                            value={contractInsurance?.receiptNumber || ""}
                            placeholder=""
                            onChange={handleChange}
                        />
                    </div> */}
                    <div className="form-element">
                        <input
                            type="text"
                            name="contractInsurance.prefixNumber"
                            value={contractInsurance?.prefixNumber || ""}
                            placeholder=""
                            onChange={handleChange}
                            disabled={true}
                        />
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            name="contractInsurance.receiptAddNumber"
                            value={contractInsurance?.receiptAddNumber || ""}
                            placeholder=""
                            onChange={handleChange}
                            disabled={true}
                        />
                    </div>
                </div>
            </div>


            {
                showInsuranceManager ? (
                    <>
                        <div className="item-row" data-grow="3">
                            <div className="row-head">
                                <span>팩스번호</span>
                            </div>
                            <div className="row-content">
                                <div className="form-element">
                                    <input
                                        type="text"
                                        name="contractInsurance.faxNumber"
                                        value={contractInsurance?.faxNumber || ""}
                                        placeholder=""
                                        onChange={handleChange}
                                        onInput={onlyNumericInput}
                                        maxLength={11}
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="item-row" data-grow="3">
                            <div className="row-head">
                                <span>담당자</span>
                            </div>
                            <div className="row-content">
                                <div className="form-element">
                                    <input
                                        type="text"
                                        name="contractInsurance.managerName"
                                        value={contractInsurance?.managerName || ""}
                                        placeholder=""
                                        onChange={handleChange}
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="item-row" data-grow="3">
                            <div className="row-head">
                                <span>담당자 연락처</span>
                            </div>
                            <div className="row-content">
                                <div className="form-element">
                                    <input
                                        type="text"
                                        name="contractInsurance.managerPhone"
                                        value={contractInsurance?.managerPhone || ""}
                                        placeholder=""
                                        onChange={handleChange}
                                        onInput={onlyNumericInput}
                                        maxLength={11}
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="item-row" data-grow="3"></div>
                    </>
                ) : (
                    <></>
                )
            }


        </>
    );
};
